import React, { FC, ReactNode, useMemo } from 'react';

import { EvSimpleBarChart, renderCustomTooltip } from '@evinced-private/ui-common';
import { EVINCED_SEVERITIES, EVINCED_SEVERITIES_IDS } from '@evinced-private/ui-common/services';

import { MobileChartTableLink } from 'src/common/components/mobile-chart-table-link/MobileChartTableLink';
import { SeverityCellFormatter } from 'src/common/components/severity-cell-formatter/SeverityCellFormatter';
import { ScansImpactedBySeverityChartData } from 'src/common/types/MobileSingleSessionTypes';

import {
	customSrSummaryRenderer,
	mobileSeverityPatterns,
	prepareDataForScansImpactedBySeverityChart
} from '../../helpers/MobileChartsHelper';

import './MobileScansImpactedBySeverityChart.scss';

export interface ISeverityCountType {
	severity: EVINCED_SEVERITIES_IDS;
	name: EVINCED_SEVERITIES;
	count: number;
	scanCount: number;
	id: EVINCED_SEVERITIES_IDS;
}

interface IMobileScansImpactedBySeverityChartProps {
	severitiesCount: ISeverityCountType[];
	onBarClick: (severity: string) => void;
}

export const MobileScansImpactedBySeverityChart: FC<IMobileScansImpactedBySeverityChartProps> = ({
	severitiesCount = [],
	onBarClick
}) => {
	const barData = useMemo(
		() => prepareDataForScansImpactedBySeverityChart(severitiesCount),
		[severitiesCount]
	);

	const severityFieldFormatter = (
		cell: string,
		row: ScansImpactedBySeverityChartData
	): ReactNode => {
		return (
			<MobileChartTableLink
				value={cell}
				onClick={() => onBarClick(row.severity)}
				customLink={<SeverityCellFormatter severity={cell} />}
			/>
		);
	};

	return (
		<EvSimpleBarChart
			title="Scans impacted per Severity"
			data={barData}
			patterns={mobileSeverityPatterns}
			className="mobile-scans-impacted-per-severity-chart"
			width="100%"
			height={400}
			pointWidth={30}
			headerTooltipProps={{
				tooltipText: 'This chart shows the number of scans impacted by each severity level.',
				tooltipTitle: 'Severity Chart Information',
				tooltipPlacement: 'right'
			}}
			customSrSummaryRenderer={() =>
				customSrSummaryRenderer(barData, 'Scans impacted per Severity', 'scan')
			}
			customTooltip={(context: { y: number; name: string }): string => {
				const { y, name } = context;
				return renderCustomTooltip(y, name, 'Scan');
			}}
			onBarClick={(point) => onBarClick(point.options?.id || EVINCED_SEVERITIES.NEEDS_REVIEW)}
			withLegend={false}
			isFocusable={true}
			isInteractive={true}
			enableFocusBorder={true}
			tableProps={{
				dataType: 'Severity',
				columnsPattern: [
					{
						name: 'Severity',
						field: 'label',
						formatter: severityFieldFormatter
					},
					{
						name: 'Total Scans',
						field: 'count'
					}
				]
			}}
		/>
	);
};
